import { type ReactNode, type SyntheticEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { Block } from '@/api/courses';
import { isAnswerGesture, settleAnswer, touchAnswer } from '@/lib/lessonAnswerGuard';
import { isStoreBuild } from '@/lib/platform';
import { reviewAnswerText } from '@/lib/reviewAnswerText';
import { queuedLabelKey } from '@/lib/queuedLabel';
import { ANSWERABLE_TYPES, OPEN_TASK_TYPES } from '@/lib/sectionCompletion';
import { cn } from '@/lib/utils';
import { renderBlock, type RenderProps } from '@/pages/student/Lesson/blockRenderers';

import { ReviewCopyButton } from './ReviewCopyButton';

export type BlockViewMode = 'student' | 'preview' | 'edit' | 'review';

/**
 * Как ученик прошёл это задание — для разбора куратором и админом.
 *
 * ⚠️ ПОПЫТКИ ЗДЕСЬ НЕ УКРАШЕНИЕ. Признак «верно» липкий: ответив правильно с
 * третьей попытки, ученик получает в базе «верно», и по одной галочке разницы
 * с «понял сразу» не видно. Счётчик — единственное, что её показывает.
 */
export type BlockReview = {
  /** Сколько раз ученик отвечал. */
  attempts: number;
  /** Когда отвечал в последний раз (ISO). */
  answeredAt?: string;
  /**
   * Задание отвечаемое и непустое, но ответа нет — ученик до него не дошёл.
   * Отличать это от «ответил неверно» обязательно: иначе куратор увидит
   * ошибку там, где человек просто не дошёл до задания.
   */
  unanswered?: boolean;
};

export type BlockViewProps = {
  block: Block;
  mode?: BlockViewMode;
  /** Student mode: interactive answers flow back through this. */
  onAnswer?: RenderProps['onAnswer'];
  /**
   * Ответ принят приложением, но сервер его ещё не подтвердил — лежит в очереди
   * отправки. Показываем это честно: раньше в такой ситуации ученик видел
   * галочку «сохранено», хотя до куратора работа не доходила.
   */
  pending?: boolean;
  /** Previously-submitted answer, so the block restores after a page refresh. */
  saved?: RenderProps['saved'];
  /** Только для mode='review': как ученик прошёл это задание. */
  review?: BlockReview;
  /** Сервер назвал задание незаполненным в отказе `409 open_answers_missing`. */
  missing?: RenderProps['missing'];
};

/**
 * BlockView — the SINGLE entry point for rendering a lesson block across the
 * student player, the builder canvas and previews. Replaces the old split
 * where the builder drew a config form (blockRegistry.renderEditor) and the
 * student saw a different, real block (blockRenderers) — the divergence that
 * made the constructor feel "off". Now everyone renders the same thing.
 *
 * `mode` controls interactivity, not appearance:
 *   - student — fully interactive; answers ship through `onAnswer`.
 *   - preview — identical visual, but inert (pointer events off, no answer
 *     wiring). Used by the builder canvas and the preview pane.
 *   - edit    — same visual as preview today; per-type INLINE editors land
 *     incrementally (phase 2). Until a type gets its edit branch, the builder
 *     shows this preview plus the legacy field editor, so nothing regresses.
 *   - review  — разбор ответов ученика куратором или админом: тот же блок с
 *     подставленным ответом ученика, но отвечать нельзя. Зелёное и красное
 *     достаются даром — рендерер раскрывает разбор именно по наличию `saved`.
 */
export function BlockView({ block, mode = 'student', onAnswer, saved, pending, review, missing }: BlockViewProps): ReactNode {
  const { t } = useTranslation();

  // Разбор ответов ученика: показываем ЕГО ответ, ввод закрыт.
  //
  // ⚠️ `onAnswer` СЮДА НЕ ПЕРЕДАЁТСЯ ВООБЩЕ. Не «передаётся и игнорируется», а
  // именно не передаётся: любой ответ, ушедший отсюда, записался бы на ученика
  // от лица смотрящего — куратор подделал бы прогресс, сам того не заметив.
  if (mode === 'review') {
    // ⚠️ НЕТ СВЕДЕНИЙ — НЕТ И ВЕРДИКТА. Текст, картинка, видео отвечаемыми не
    // бывают, и «верно/неверно» к ним неприменимо. Без этой ветки над каждой
    // картинкой урока висела бы красная плашка «Неверно» — куратор читал бы её
    // как ошибку ученика там, где отвечать было нечего. Поймано на стенде.
    if (!review) {
      return (
        <div className="review-inert" aria-readonly>
          {renderBlock({ block, saved, readOnly: true })}
        </div>
      );
    }
    const copyable = reviewAnswerText(block, saved);
    return (
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          {review?.unanswered ? (
            <span className="inline-flex items-center gap-1.5 rounded-pill bg-surface-container-highest px-3 py-1 text-xs font-semibold text-on-surface-variant">
              {/* Пустой квадратик, а не крестик: «не заполнено» — это не
                  «ошибся». Значок обязан быть из вырезанного шрифта, иначе на
                  экране появится его имя словами. */}
              <span className="material-symbols-outlined text-sm">check_box_outline_blank</span>
              {t('blockReview.unanswered')}
            </span>
          ) : OPEN_TASK_TYPES.has(block.type) ? (
            // Открытое задание проверяет куратор: «Верно» над сочинением или ответом по пунктам говорило бы, что
            // ответ уже оценён (слово владельца 13.10.2026). Зелёный и красный — только для «верно / неверно».
            <span className="inline-flex items-center gap-1.5 rounded-pill bg-primary-fixed px-3 py-1 text-xs font-semibold text-on-primary-fixed-variant">
              <span className="material-symbols-outlined text-sm">task_alt</span>
              {t('blockReview.sent')}
            </span>
          ) : (
            <span
              className={cn(
                'inline-flex items-center gap-1.5 rounded-pill px-3 py-1 text-xs font-semibold',
                saved?.is_correct
                  ? 'bg-success-container text-on-success-container'
                  : 'bg-error-container text-on-error-container',
              )}
            >
              <span className="material-symbols-outlined text-sm">
                {saved?.is_correct ? 'check_circle' : 'cancel'}
              </span>
              {saved?.is_correct ? t('blockReview.correct') : t('blockReview.wrong')}
            </span>
          )}

          {/* Попытки показываем всегда, когда их больше одной: именно они
              отличают «понял сразу» от «дожал с третьего раза». */}
          {!review?.unanswered && (review?.attempts ?? 0) > 1 && (
            <span className="inline-flex items-center gap-1.5 rounded-pill bg-warning-container px-3 py-1 text-xs font-semibold text-on-warning-container">
              <span className="material-symbols-outlined text-sm">refresh</span>
              {t('blockReview.attempts', { count: review?.attempts ?? 0 })}
            </span>
          )}

          {!review?.unanswered && review?.answeredAt && (
            <span className="text-xs text-on-surface-variant">
              {t('blockReview.answeredAt', {
                when: new Date(review.answeredAt).toLocaleString('ru-RU'),
              })}
            </span>
          )}

          {/* «Скопировать» — у написанного учеником текста (13.10.2026): сочинение, ввод в пропуски. В ряду
              плашек, а не внутри `.review-inert`: там события погашены, и кнопку нельзя было бы нажать. */}
          {!review?.unanswered && copyable !== null && <ReviewCopyButton text={copyable} />}
        </div>

        {/* readOnly: сочинение показывается текстом целиком — поле внутри `.review-inert` не прокручивается. */}
        <div className="review-inert" aria-readonly>
          {renderBlock({ block, saved, readOnly: true })}
        </div>
      </div>
    );
  }

  if (mode === 'student') {
    // Начатый ответ в приложении (окно «Выйти из урока?», `lib/lessonAnswerGuard.ts`). Только тестовые задания:
    // у сочинения свой черновик, у записи — свой сторож. На сайте не следим вовсе.
    const answer = onAnswer;
    const watched =
      !!answer && isStoreBuild() && ANSWERABLE_TYPES.has(block.type) && !OPEN_TASK_TYPES.has(block.type);
    const content = studentContent(
      watched && answer
        ? (payload) => {
            settleAnswer(block.id);
            return answer(payload);
          }
        : answer,
    );
    return watched ? <AnswerWatch blockId={block.id}>{content}</AnswerWatch> : content;
  }

  function studentContent(onAnswer: RenderProps['onAnswer']): ReactNode {
    // When we have a saved answer (only answerable blocks get one), show a
    // restored indicator so a refresh clearly preserves the student's work, and
    // pass `saved` down so renderers that support it rehydrate their UI inline.
    if (saved) {
      // ⚠️ У ОТКРЫТЫХ ЗАДАНИЙ ПИЛЮЛИ «ОТВЕТ СОХРАНЁН» НЕТ (11.09.2026). Сочинение и
      // запись показывают своё состояние сами, и только они знают, что сохранённая
      // запись короче 5 секунд или сочинение пустое. Зелёное «Ответ сохранён · верно»
      // рядом с красной причиной говорило бы ученику обратное.
      const ownState = OPEN_TASK_TYPES.has(block.type);
      return (
        <div className="space-y-2">
          {!ownState && (
            <span
              className={cn(
                'inline-flex items-center gap-1.5 rounded-pill px-3 py-1 text-xs font-semibold',
                saved.is_correct
                  ? 'bg-success-container text-on-success-container'
                  : 'bg-warning-container text-on-warning-container',
              )}
            >
              <span className="material-symbols-outlined text-sm">
                {saved.is_correct ? 'check_circle' : 'history'}
              </span>
              {saved.is_correct ? t('blockView.savedCorrect') : t('blockView.saved')}
            </span>
          )}
          {pending && (
            <span className="ml-2 inline-flex items-center gap-1.5 rounded-pill bg-surface-container-highest px-3 py-1 text-xs font-semibold text-on-surface-variant">
              <span className="material-symbols-outlined text-sm">cloud_off</span>
              {t(queuedLabelKey(block.type))}
            </span>
          )}
          {renderBlock({ block, onAnswer, saved, missing })}
        </div>
      );
    }
    return renderBlock({ block, onAnswer, missing });
  }
  // preview / edit: render the real block but inert so it looks exactly like
  // the student view without accepting input.
  return (
    <div className={cn(mode !== 'edit' && 'pointer-events-none select-none')} aria-readonly>
      {renderBlock({ block })}
    </div>
  );
}

/**
 * Прозрачная обёртка задания (`display: contents` — раскладку не трогает): ввод, выбор, нажатие на орган
 * управления или перетаскивание отмечают ответ начатым; задание ушло с экрана — отметка снимается.
 */
function AnswerWatch({ blockId, children }: { blockId: string; children: ReactNode }): ReactNode {
  useEffect(() => () => settleAnswer(blockId), [blockId]);
  const onGesture = (e: SyntheticEvent) => {
    if (isAnswerGesture(e.target)) touchAnswer(blockId);
  };
  const onEdit = () => touchAnswer(blockId);
  return (
    <div className="contents" onClickCapture={onGesture} onDropCapture={onEdit} onInputCapture={onEdit} onChangeCapture={onEdit}>
      {children}
    </div>
  );
}
